import { useFieldArray, useForm } from 'react-hook-form';
import './add-mid.part-form.scss';
import { AddMidForm, CommissionType, CommissionValueType, ProcessorData } from './add-mid.types';
import AppFormInput from '../../../../components/form/app-form-input';
import { UserState } from '../../../../store/user.slice';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { apiService } from '../../../../services/api.service';
import { AppStore } from '../../../../store/store';
import AppFormSelect from '../../../../components/form/app-form-select';
import AddMidPartFormCommissionField from './add-mid.part.form.commission-field';
import Button from '../../../../components/button';
import { useNavigate, useParams } from 'react-router-dom';
import { errorService } from '../../../../services/error.service';
import { CommissionApiResponse, MerchantMid } from '../../../../services/api.service-types';

const AddMidPartForm = () => {
    const navigate = useNavigate();

    const { id } = useParams();

    const user = useSelector<AppStore, UserState>(state => state.user)

    const [processors, setProcessors] = useState<ProcessorData[]>();

    const form = useForm<AddMidForm>({
        defaultValues: {
            processorId: '',
            midName: '',
            md5: '',
            midCode: '',
            privateKey: '',
            commissions: Object.values(CommissionType).map((type) => ({
                value: 0,
                valueType: CommissionValueType.PERCENT,
                type,
                anyValue: false,
            })),
        },
    });

    const {
        fields: commissionFields,
    } = useFieldArray({
        control: form.control,
        name: `commissions` as const,
    });

    useEffect(() => {
        const getProcessors = async () => {
            try {
                const response = await apiService.getAllProcessors();
                return response.data;
            } catch (e) {
                return []
            }
        }
        getProcessors().then((data) => {
            setProcessors(data)
        })
    }, [user])

    const processorsOptions = processors && processors?.map((processor) => {
        return {
            label: `${processor.processorName} (${processor.processorType})`,
            value: processor.id
        }
    })

    const handleAdd = async (data: AddMidForm) => {
        if (!id) return
        try {
            const midRequestBody: MerchantMid = {
                mid: {
                    processorId: data.processorId,
                    merchantId: id,
                    midCode: data.midCode,
                    trashHoldLimit: data.trashHoldLimit ?? 0,
                    midName: data.midName,
                    md5: data.md5,
                    privateKey: data.privateKey,
                    midDescriptor: data.midDescriptor,
                    valid: true,
                },
                commissions: data.commissions.map((c) => ({
                    commissionType: c.type,
                    commissionAmount: c.value,
                    isPercent: c.valueType === CommissionValueType.PERCENT,
                    isFixed: c.valueType === CommissionValueType.FIXED,
                }as Partial<CommissionApiResponse>)) ,
            };
            await apiService.createMid(id, midRequestBody);
            errorService.showSuccess('Success', `Mid ${data.midName} has been added!`)
            navigate(-1);
        } catch (e) {
            errorService.showApiError(e);
        }
    }

    return (
        <div className="add-mid-part-form flex-column flex-gap-30">
            <div className='flex-row flex-gap-20'>
                <AppFormInput
                    control={form.control}
                    name="midName"
                    placeholder="MID name"
                    validations={{ required: true }}
                />
                <AppFormInput
                    control={form.control}
                    name="trashHoldLimit"
                    type="number"
                    placeholder="Cap MID - $"
                    validations={{ required: true }}
                    hasCurrency={true}
                />
                <AppFormInput
                    control={form.control}
                    name="md5"
                    placeholder="MD-5"
                    validations={{ required: true }}
                />
                <AppFormSelect
                    control={form.control}
                    name="processorId"
                    options={processorsOptions || []}
                    placeholder="Select Processor"
                    validations={{ required: true }}
                />
            </div>
            <div className="flex-row flex-gap-20">
                <AppFormInput
                    control={form.control}
                    name="privateKey"
                    placeholder="Public Key"
                    validations={{ required: true }}
                />
                <AppFormInput
                    control={form.control}
                    name="midCode"
                    placeholder="Account Code"
                    validations={{ required: true }}
                />
                <AppFormInput
                    control={form.control}
                    name="midDescriptor"
                    placeholder="MID Descriptor"
                />
            </div>
            <div className="flex-row flex-gap-20 flex-wrap">
                {commissionFields.map((field, index) => (
                    <AddMidPartFormCommissionField
                        key={field.id}
                        form={form}
                        label={`Transaction Commission`}
                        secondaryLabel={field.type}
                        commissionName={`commissions.${index}` as const}
                    />
                ))}
            </div>
            <div className='flex-row flex-align-end-end flex-gap-40'>
                <Button
                    label='Cancel'
                    theme='secondary'
                    onClick={() => navigate(-1)}
                />
                <Button
                    label='Save'
                    disabled={!form.formState.isValid}
                    onClick={form.handleSubmit(handleAdd)}
                />
            </div>
        </div>
    )
}

export default AddMidPartForm;